




import Enumerable from "linq";
import { Component, Mixins, Prop } from "vue-property-decorator";
import {
  Document,
  ItemSet,
  Section,
  AutoNotePageKinds,
} from "../../../../domain/document";
import {
  IFontSizeAutoAdjustable,
  IExtraPageRelationSettable,
  instanceOfIExtraPageRelationSettable,
  instanceOfIFontSizeAutoAdjustable,
  TextBoxItem,
} from "../../../../domain/document/items";
import { FontSizeCalculator } from "../../../../domain/drawing";
import { ModelEditableMixin } from "../../../system/mixins";
import { ExtraPageDialogsAccessor } from "../CommonComponentsAccessor";
import { DocumentStructureEditableMixin, ExtraDocumentEditableMixin } from "../mixins";
import { EditingDocument } from "..";
import { IConfiguration } from "../../../../domain/configuration";
import { DateFormatter } from "../../../../domain/system";

export enum pageObjectType {
  Create = 0,
  Update = 1,
}

@Component
export default class OverflowedTextPageExtender extends Mixins(ModelEditableMixin, DocumentStructureEditableMixin, ExtraDocumentEditableMixin) {
  editingSource: EditingDocument;
  
  @Prop()
  dialogs: ExtraPageDialogsAccessor;

  async addAllOverflowedTextPage(): Promise<void> {
    const config = (this.$store.getters.configuration as IConfiguration).behavior.document.editor.extraPageAdditionAtInitialization;

    // 途中に追加されるとまずいのであらかじめconcatしておく
    const sections = this.editingSource.sections.concat();
    for (const section of sections) {
      const itemSet = this.editingSource.itemSets[section.itemSetId];
      for (const privateId of section.itemPrivateIds) {
        const item = itemSet.items[privateId];
        if (!instanceOfIFontSizeAutoAdjustable(item)) { continue; }
        if (!item.isFreeInputable) {
          // 固定文字も続紙追加ダイアログが出てしまうので除外している
          continue;
        }
        if (!item.isVisible) {
          // 非表示のオブジェクトでも続紙追加ダイアログが出てしまうので除外している
          continue;
        }
        const updatedSectionIndex = this.getSectionIndex(section);
        if (updatedSectionIndex < 0) {
          console.warn("更新対象のSectionが見つかりませんでした。");
          continue;
        }
        if (config.isTargetOnly && !config.targets.some(n => n === item.name)) {
          // 対象のみの指定になっていて、なおかつ対象のオブジェクトではない
          continue;
        }
        const isUpdated = await this.addOverflowedTextPage(section.itemSetId, updatedSectionIndex, item);
        if (isUpdated) {
          await this.$nextTick();
        }
      }
    }
  }

  getSectionIndex(section: Section): number {
    if (!section) { return -1; }
    for (let sectionIndex = 0; sectionIndex < this.editingSource.sections.length; ++sectionIndex) {
      if (this.editingSource.sections[sectionIndex] === section) { return sectionIndex; }
    }
    return -1;
  }

  async addOverflowedTextPage(itemSetId: number, sectionIndex: number, item: IFontSizeAutoAdjustable, type: pageObjectType = pageObjectType.Update): Promise<boolean> {
    if (!this.isExtraPageAddible) {
      return false;
    }
    if (!item.autoNotePageSize) {
      return false;
    }
    if (item.isEmpty) {
      return false;
    }
    if (!item.isVisible) {
      // 非表示のオブジェクトからは続紙追加ダイアログを表示させない
      return false;
    }
    if (!item.isMultiLine) {
      // 複数行オブジェクト以外は続紙追加ダイアログを表示させない
      return false;
    }
    if (!item.textStyle.isAdjustedFontSize) {
      return false;
    }
    if (item.autoNotePageSize < item.textStyle.displayFontSize) {
      return false;
    }
    if (item.autoNotePageKind === AutoNotePageKinds.Excess && !this.isInExcess(item)) {
      return false;
    }
    const message = (item.autoNotePageKind === AutoNotePageKinds.Excess)
      ? "文字が入力するエリアに収まらないため、超過分を続紙に記載します。"
      : "文字を縮小しても入力するエリアに収まらないため、続紙に記載します。";
    const extraPageRelation = item.clone() as IExtraPageRelationSettable;
    if (this.hasRelatedItemInExtraPage(extraPageRelation) && type === pageObjectType.Update) {
      // 追加済みの続紙のオブジェクトの値を更新
      const isConfirmed = await this.isConfirmed("続紙上書き確認", message + "続紙の内容を上書きしてもよろしいですか？", "上書きする", true);
      if (!isConfirmed) {
        return false;
      }
      await this.updateExtraPageObject(itemSetId, sectionIndex, item, extraPageRelation);
      const relatedSectionIndex = this.getRelatedSectionIndex(extraPageRelation);
      if (relatedSectionIndex) {
        await this.$nextTick();
        // updateItemとタイミングがかぶるので遅らせている
        this.currentSectionIndex = relatedSectionIndex;
      } else {
        this.$warn("related section not found!!!", "related section not found!!!");
      }
      return true;
    } else {
      // 続紙を新規に追加
      const nextSectionIndex = sectionIndex + 1;
      const sectionLength = this.sections.length;
      const destinationSectionIndex = this.isExtendedPagePositionSelectable
        ? await this.dialogs.positionSelector.show(message, nextSectionIndex, sectionLength)
        : ((await this.isConfirmed("続紙追加確認", message + "最終ページの次に続紙を追加してもよろしいですか？", "追加する", false)) ? sectionLength : null);
      if (destinationSectionIndex === null || destinationSectionIndex === undefined) { return false; }
      return this.performAddExtraPage(itemSetId, destinationSectionIndex, item);
    }
  }

  private async isConfirmed(title: string, message: string, buttonText: string, isDanger: boolean): Promise<boolean> {
    const index = await this.$commonDialogs.confirmation.show({
      title: title,
      message: [
        message,
      ],
      buttons: [
        {
          kind: isDanger ? "danger" : "confirm",
          label: buttonText,
        },
      ],
      cancellation: {
        isEnable: true,
      },
    });
    return (index === 0);
  }

  isInExcess(item: IFontSizeAutoAdjustable): boolean {
    const splitted = this.splitExcessText(item);
    return (splitted.length !== 1);
  }

  /**
   * 表示文字サイズと続紙追加文字サイズを比較して、大きいほうの文字サイズで計算する
   */
  splitExcessText(item: IFontSizeAutoAdjustable): string[] {
    // とりあえずは複数行しかないものとして実装する
    const isMultiLine = true;
    const fontSize = (item.textStyle.displayFontSize > item.autoNotePageSize)
      ? item.textStyle.displayFontSize
      : item.autoNotePageSize;
    return FontSizeCalculator.splitExcessText(
      item.text(),
      fontSize,
      item.textStyle.lineHeight,
      item.geometry.rectangle.width,
      item.geometry.rectangle.height,
      isMultiLine);
  }

  async updateExtraPageObject(extraSourceItemSetId: number, destinationSectionIndex: number, extraSourceItem: IFontSizeAutoAdjustable, extraPageRelation: IExtraPageRelationSettable): Promise<void> {
    const found = this.findRelatedExtraItemSetIndex(extraPageRelation);
    if (found < 0) {
      throw new Error("続紙の更新に失敗しました。");
    }
    for (const item of this.itemSets[found].items) {
      if (!instanceOfIExtraPageRelationSettable(item)) { continue; }
      if (!this.isRelatedObject(item, extraPageRelation)) { continue; }
      if (extraSourceItem.autoNotePageKind === AutoNotePageKinds.Excess) {
        const texts = this.splitExcessText(extraSourceItem);
        if (texts.length === 1) { throw new Error("テキストの分割に失敗しました。"); }
        await this.addOverflowedTextPage(
          found,
          destinationSectionIndex,
          this.updateExtraDestinationObject(
            item,
            found,
            texts[1],
            this.updateExtraSourceObject(extraSourceItem as IExtraPageRelationSettable & IFontSizeAutoAdjustable, extraSourceItemSetId, texts[0])
          ),
          pageObjectType.Update);
      } else {
        await this.addOverflowedTextPage(
          found,
          destinationSectionIndex,
          this.updateExtraDestinationObject(
            item,
            found,
            extraSourceItem.text(),
            this.updateExtraSourceObject(extraSourceItem as IExtraPageRelationSettable & IFontSizeAutoAdjustable, extraSourceItemSetId, "続紙を参照")
          ),
          pageObjectType.Update);
      }
      return;
    }
  }

  updateExtraSourceObject(source: IExtraPageRelationSettable & IFontSizeAutoAdjustable, itemSetId: number, text: string): IExtraPageRelationSettable & IFontSizeAutoAdjustable {
    const cloned = source.clone() as IExtraPageRelationSettable & IFontSizeAutoAdjustable;
    cloned.setText(text);
    cloned.adjustFontSize();
    this.updateItem(cloned, itemSetId);
    return cloned;
  }

  updateExtraDestinationObject(destination: IExtraPageRelationSettable, itemSetId: number, text: string, source: IExtraPageRelationSettable): IExtraPageRelationSettable & IFontSizeAutoAdjustable {
    const cloned = destination.clone() as IExtraPageRelationSettable & IFontSizeAutoAdjustable;
    cloned.value = (source.relationObjectName ? source.relationObjectName + "\r\n" : "") + text;
    cloned.adjustFontSize();
    // TODO: 続紙に文書内転記とか外部処理が仕込んである場合は見直しが必要
    this.updateItem(cloned, itemSetId);
    return cloned;
  }

  private getRelatedSectionIndex(extraPageRelation: IExtraPageRelationSettable): number {
    for (let sectionIndex = 0; sectionIndex < this.sections.length; ++sectionIndex) {
      const section = this.sections[sectionIndex];
      if (!section || section.itemSetId === 0) { continue; }
      for (const privateId of section.itemPrivateIds) {
        const item = this.itemSets[section.itemSetId].items[privateId];
        if (!instanceOfIExtraPageRelationSettable(item)) { continue; }
        if (this.isRelatedObject(item, extraPageRelation)) { return sectionIndex; }
      }
    }
    return null;
  }

  hasRelatedItemInExtraPage(extraItem: IExtraPageRelationSettable): boolean {
    return (0 <= this.findRelatedExtraItemSetIndex(extraItem));
  }

  findRelatedExtraItemSetIndex(extraItem: IExtraPageRelationSettable): number {
    const found = Enumerable.from(this.itemSets)
      .select((s, i) => {
        return {
          index: i,
          itemSet: s,
        };
      })
      .skip(1)  // 先頭のitemSetは続紙じゃないので除外.
      .firstOrDefault(s => this.isRelatedItemSet(s.itemSet, extraItem));
    return !found
      ? -1
      : found.index;
  }

  isRelatedItemSet(itemSet: ItemSet, item: IExtraPageRelationSettable): boolean {
    return Enumerable.from(itemSet.items)
      .any(i => instanceOfIExtraPageRelationSettable(i) && this.isRelatedObject(i, item));
  }

  isRelatedObject(item: IExtraPageRelationSettable, extraPageRelation: IExtraPageRelationSettable): boolean {
    // relatedObjectKeyとrelationObjectKeyは、続紙から文字がはみ出る場合にさらに続紙を追加できるようにする対応で追加した
    // その対応が入る前の文書のオブジェクトはnameとidで紐づきをチェックする
    return !!extraPageRelation
        && !!item
        && !!item.isNote
        && (!!item.relatedObjectKey && !!extraPageRelation.relationObjectKey)
          ? (item.relatedObjectKey === extraPageRelation.relationObjectKey)
          : (item.relatedObjectName === extraPageRelation.name && item.relatedObjectId === extraPageRelation.id && !item.relatedObjectKey);
  }

  private async performAddExtraPage(extraSourceItemSetId: number, destinationSectionIndex: number, extraSourceItem: IFontSizeAutoAdjustable): Promise<boolean> {
    try {
      const d = await this.dialogs.documentSelector.show(this.editingSource.attributes.patient, this.editingSource.extraFormats, this.getExtraDocument);
      if (!d) { return false; }
      if (extraSourceItem.autoNotePageKind === AutoNotePageKinds.Excess) {
        const texts = this.splitExcessText(extraSourceItem);
        if (texts.length === 1) { throw new Error("テキストの分割に失敗しました。"); }
        this.mergeExtraItem(d.itemSets[0], texts[1], extraSourceItem as IExtraPageRelationSettable & IFontSizeAutoAdjustable, extraSourceItemSetId, texts[0]);
      } else {
        this.mergeExtraItem(d.itemSets[0], extraSourceItem.text(), extraSourceItem as IExtraPageRelationSettable & IFontSizeAutoAdjustable, extraSourceItemSetId, "続紙を参照");
      }
      await this.mergeExtraDocument(d, destinationSectionIndex);
      await this.addOverflowedTextPageRecursively(d, destinationSectionIndex);
      return true;
    } catch (e) {
      this.$error(
        "続紙読込失敗。",
        "指定された続紙の情報を取得できませんでした。",
        e,
      );
      return false;
    }
  }

  private addOverflowedTextPageRecursively(extraDocument: Document, destinationSectionIndex: number): Promise<boolean> {
    const extraObject = Enumerable.from(extraDocument.itemSets)
      .selectMany(i => i.items)
      .firstOrDefault(i => i instanceof TextBoxItem && i.name === "DOCUMAKER_NOTE") as TextBoxItem;
    const itemSetId = Enumerable.from(this.sections)
      .where(i => this.itemSets[i.itemSetId].items.findIndex(s =>  instanceOfIExtraPageRelationSettable(s) && s.relatedObjectKey === extraObject.relatedObjectKey) >= 0)
      .select(p => p.itemSetId)
      .firstOrDefault();
    return this.addOverflowedTextPage(itemSetId, destinationSectionIndex, extraObject.clone() as TextBoxItem, pageObjectType.Create);
  }

  private getDisplayName(item: IFontSizeAutoAdjustable): string {
    return !!item.displayName ? item.displayName : item.name;
  }

  private mergeExtraItem(itemSet: ItemSet, text: string, extraSource: IExtraPageRelationSettable & IFontSizeAutoAdjustable, extraSourceItemSetId: number, extraSourceText: string): void {
    for (const item of itemSet.items) {
      if (!instanceOfIExtraPageRelationSettable(item)) {
        continue;
      }
      this.mergeExtraDestinationObject(item, itemSet, text, this.mergeExtraSourceObject(extraSource, extraSourceItemSetId, extraSourceText));
      return;
    }
    // DOCUMAKER_NOTEという名称のオブジェクトに値が格納される動作になっている模様
    const message = "続紙に値を格納するオブジェクトがありませんでした。";
    this.$error("続紙更新失敗", message);
    throw new Error(message);
  }

  mergeExtraSourceObject(source: IExtraPageRelationSettable & IFontSizeAutoAdjustable, itemSetId: number, text: string): IExtraPageRelationSettable & IFontSizeAutoAdjustable {
    const cloned = source.clone() as IExtraPageRelationSettable & IFontSizeAutoAdjustable;
    if (cloned.relatedObjectId) {
      cloned.relationObjectName = cloned.relatedObjectName;
      cloned.relationObjectId = cloned.relatedObjectId;
    } else {
      cloned.relationObjectName = this.getDisplayName(cloned);
      cloned.relationObjectId = cloned.id;
    }
    cloned.relationObjectKey = DateFormatter.format(new Date(), "YYYYMMDDHHmmssSSS");
    cloned.setText(text);
    cloned.adjustFontSize();
    this.updateItem(cloned, itemSetId);
    return cloned;
  }

  mergeExtraDestinationObject(destination: IExtraPageRelationSettable, itemSet: ItemSet, text: string, source: IExtraPageRelationSettable): IExtraPageRelationSettable {
    const cloned = destination.clone() as IExtraPageRelationSettable & IFontSizeAutoAdjustable;
    cloned.relatedObjectKey = source.relationObjectKey;
    cloned.relatedObjectName = source.relationObjectName;
    cloned.relatedObjectId = source.relationObjectId;
    // 続紙の続紙の先頭にDOCUMAKER_NOTEが出ないようにガード追加している
    const headerText = (!!source.relationObjectName && source.relationObjectName !== "DOCUMAKER_NOTE")
      ? source.relationObjectName + "\r\n"
      : "";
    cloned.setText(headerText + text);
    cloned.adjustFontSize();
    // TODO: 続紙に文書内転記とか外部処理が仕込んである場合は見直しが必要
    itemSet.items.splice(cloned.privateId, 1, cloned);
    return cloned;
  }
}
